import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import './WebsiteStats.css';

const DEFAULT_FILTERS = {
  startDate: '',
  endDate: '',
  search: '',
  page: 1,
  limit: 25,
  sortBy: 'activityScore',
  sortDir: 'desc'
};

const SUMMARY_CARD_CONFIG = [
  { key: 'totalUsers', label: 'Total Users', icon: 'fa-users', tone: 'primary' },
  { key: 'activeUsers', label: 'Active Users', icon: 'fa-chart-line', tone: 'success' },
  { key: 'totalPersonalMenus', label: 'Personal Menus', icon: 'fa-utensils', tone: 'info' },
  { key: 'totalMealPlans', label: 'Meal Plans', icon: 'fa-calendar-alt', tone: 'warning' },
  { key: 'totalNutritionPlans', label: 'Nutrition Plans', icon: 'fa-heartbeat', tone: 'secondary' },
  { key: 'totalSavedShoppingLists', label: 'Saved Lists', icon: 'fa-shopping-basket', tone: 'accent' },
  { key: 'totalExercises', label: 'Exercises', icon: 'fa-dumbbell', tone: 'primary' },
  { key: 'totalWorkouts', label: 'Workouts', icon: 'fa-running', tone: 'success' },
  { key: 'totalPrograms', label: 'Programs', icon: 'fa-layer-group', tone: 'info' },
  { key: 'totalWorkoutSessions', label: 'Workout Sessions', icon: 'fa-stopwatch', tone: 'warning' },
  { key: 'totalWorkoutMinutes', label: 'Workout Minutes', icon: 'fa-clock', tone: 'secondary' },
  { key: 'totalMenuViews', label: 'Menu Views', icon: 'fa-eye', tone: 'accent' },
  { key: 'totalMenuOrders', label: 'Menu Orders', icon: 'fa-shopping-cart', tone: 'primary' }
];

const TABLE_COLUMNS = [
  { key: 'name', label: 'User', sortable: true },
  { key: 'subscriptionTier', label: 'Tier', sortable: true },
  { key: 'personalMenus', label: 'Personal Menus', sortable: true },
  { key: 'mealPlans', label: 'Meal Plans', sortable: true },
  { key: 'nutritionPlans', label: 'Nutrition Plans', sortable: true },
  { key: 'savedShoppingLists', label: 'Saved Lists', sortable: true },
  { key: 'exercises', label: 'Exercises', sortable: true },
  { key: 'workouts', label: 'Workouts', sortable: true },
  { key: 'programs', label: 'Programs', sortable: true },
  { key: 'workoutSessions', label: 'Sessions', sortable: true },
  { key: 'completedWorkoutSessions', label: 'Completed', sortable: true },
  { key: 'totalWorkoutMinutes', label: 'Workout Min', sortable: true },
  { key: 'avgWorkoutMinutes', label: 'Avg Session Min', sortable: true },
  { key: 'menuViews', label: 'Menu Views', sortable: true },
  { key: 'menuOrders', label: 'Menu Orders', sortable: true },
  { key: 'lastActivityAt', label: 'Last Activity', sortable: true }
];

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value || 0);

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const WebsiteStats = () => {
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.search) params.set('search', filters.search);
    params.set('page', String(filters.page));
    params.set('limit', String(filters.limit));
    params.set('sortBy', filters.sortBy);
    params.set('sortDir', filters.sortDir);
    return params.toString();
  }, [filters]);

  const fetchWebsiteStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/website-stats?${queryString}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch website statistics');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching website stats:', err);
      setError(err.message);
      toast.error('Failed to load website statistics');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchWebsiteStats();
  }, [fetchWebsiteStats]);

  const handleApplyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      startDate: startDateInput,
      endDate: endDateInput,
      search: searchInput.trim(),
      page: 1
    }));
  };

  const handleClearFilters = () => {
    setStartDateInput('');
    setEndDateInput('');
    setSearchInput('');
    setFilters((prev) => ({
      ...prev,
      startDate: '',
      endDate: '',
      search: '',
      page: 1
    }));
  };

  const handleSort = (field) => {
    setFilters((prev) => {
      if (prev.sortBy === field) {
        return {
          ...prev,
          sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc',
          page: 1
        };
      }

      return {
        ...prev,
        sortBy: field,
        sortDir: 'desc',
        page: 1
      };
    });
  };

  const handleLimitChange = (event) => {
    const nextLimit = parseInt(event.target.value, 10);
    setFilters((prev) => ({
      ...prev,
      limit: Number.isNaN(nextLimit) ? 25 : nextLimit,
      page: 1
    }));
  };

  const handlePageChange = (nextPage) => {
    if (!stats?.meta) return;
    if (nextPage < 1 || nextPage > stats.meta.totalPages) return;
    setFilters((prev) => ({ ...prev, page: nextPage }));
  };

  const getSortIndicator = (field) => {
    if (filters.sortBy !== field) return '';
    return filters.sortDir === 'asc' ? ' ^' : ' v';
  };

  const renderSummaryValue = (key, value) => {
    if (key === 'totalWorkoutMinutes') {
      return formatNumber(Math.round(value || 0));
    }
    return formatNumber(value);
  };

  if (loading && !stats) {
    return (
      <div className="website-stats loading-state">
        <div className="loading-spinner"></div>
        <p>Loading website statistics...</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="website-stats error-state">
        <p>{error}</p>
        <button onClick={fetchWebsiteStats} className="retry-button">
          <i className="fas fa-redo"></i> Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { summary, breakdowns, perUserStats, meta } = stats;
  const canGoPrev = meta.page > 1;
  const canGoNext = meta.page < meta.totalPages;

  return (
    <div className="website-stats">
      <div className="stats-header">
        <div>
          <h2>
            <i className="fas fa-globe"></i> Website Stats
          </h2>
          <p className="stats-subtitle">
            Platform-wide totals and per-user usage across menu, meal plan, nutrition, shopping, and fitness data.
          </p>
          {meta.hasDateFilter && (
            <p className="filter-scope">
              Date range: {formatDateTime(meta.startDate)} to {formatDateTime(meta.endDate)}
            </p>
          )}
        </div>
        <button className="refresh-button" onClick={fetchWebsiteStats} disabled={loading}>
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      <div className="website-stats-filters">
        <div className="filter-group">
          <label htmlFor="website-start-date">Start Date</label>
          <input
            id="website-start-date"
            type="date"
            value={startDateInput}
            onChange={(event) => setStartDateInput(event.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="website-end-date">End Date</label>
          <input
            id="website-end-date"
            type="date"
            value={endDateInput}
            onChange={(event) => setEndDateInput(event.target.value)}
          />
        </div>

        <div className="filter-group filter-search">
          <label htmlFor="website-user-search">Search User</label>
          <input
            id="website-user-search"
            type="text"
            placeholder="Name, email, or user ID"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="apply-filter-btn" disabled={loading}>
            Apply
          </button>
          <button onClick={handleClearFilters} className="clear-filter-btn" disabled={loading}>
            Clear
          </button>
        </div>
      </div>

      {meta.hasDateFilter && summary.newUsersInRange !== null && (
        <div className="range-summary-card">
          <span className="range-summary-title">New Users In Selected Range</span>
          <span className="range-summary-value">{formatNumber(summary.newUsersInRange)}</span>
        </div>
      )}

      <div className="stats-summary-grid">
        {SUMMARY_CARD_CONFIG.map((card) => (
          <div key={card.key} className={`stat-card ${card.tone}`}>
            <div className="stat-icon">
              <i className={`fas ${card.icon}`}></i>
            </div>
            <div className="stat-content">
              <h3>{renderSummaryValue(card.key, summary[card.key])}</h3>
              <p>{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="stats-section two-column">
        <div className="stats-subsection">
          <h3 className="section-header">
            <i className="fas fa-tags"></i> Subscription Tiers
          </h3>
          <div className="pill-list">
            {breakdowns.subscriptionTiers.map((entry) => (
              <span key={entry.tier} className="data-pill">
                <strong>{entry.tier}</strong>: {formatNumber(entry.count)}
              </span>
            ))}
          </div>
        </div>

        <div className="stats-subsection">
          <h3 className="section-header">
            <i className="fas fa-user-shield"></i> Role Breakdown
          </h3>
          <div className="pill-list">
            <span className="data-pill"><strong>Admins</strong>: {formatNumber(breakdowns.roles.admins)}</span>
            <span className="data-pill"><strong>Affiliates</strong>: {formatNumber(breakdowns.roles.affiliates)}</span>
            <span className="data-pill"><strong>Business Owners</strong>: {formatNumber(breakdowns.roles.businessOwners)}</span>
            <span className="data-pill"><strong>Business Employees</strong>: {formatNumber(breakdowns.roles.businessEmployees)}</span>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <div className="table-controls">
          <h3 className="section-header">
            <i className="fas fa-table"></i> Per-User Usage Stats
          </h3>
          <div className="table-control-group">
            <label htmlFor="rows-per-page">Rows</label>
            <select id="rows-per-page" value={filters.limit} onChange={handleLimitChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="stats-table-wrapper">
          {perUserStats.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-database"></i>
              <p>No user data found for the current filters.</p>
            </div>
          ) : (
            <table className="stats-table">
              <thead>
                <tr>
                  {TABLE_COLUMNS.map((column) => (
                    <th key={column.key}>
                      {column.sortable ? (
                        <button
                          className="sort-button"
                          onClick={() => handleSort(column.key)}
                          type="button"
                        >
                          {column.label}{getSortIndicator(column.key)}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perUserStats.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <div className="user-name">{row.name}</div>
                      <div className="user-meta">{row.email || row.userId || '-'}</div>
                    </td>
                    <td>
                      <span className="tier-badge">{row.subscriptionTier}</span>
                    </td>
                    <td>{formatNumber(row.personalMenus)}</td>
                    <td>{formatNumber(row.mealPlans)}</td>
                    <td>{formatNumber(row.nutritionPlans)}</td>
                    <td>{formatNumber(row.savedShoppingLists)}</td>
                    <td>{formatNumber(row.exercises)}</td>
                    <td>{formatNumber(row.workouts)}</td>
                    <td>{formatNumber(row.programs)}</td>
                    <td>{formatNumber(row.workoutSessions)}</td>
                    <td>{formatNumber(row.completedWorkoutSessions)}</td>
                    <td>{formatNumber(row.totalWorkoutMinutes)}</td>
                    <td>{formatNumber(row.avgWorkoutMinutes)}</td>
                    <td>{formatNumber(row.menuViews)}</td>
                    <td>{formatNumber(row.menuOrders)}</td>
                    <td>{formatDateTime(row.lastActivityAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="table-pagination">
          <button onClick={() => handlePageChange(meta.page - 1)} disabled={!canGoPrev || loading}>
            Previous
          </button>
          <span>
            Page {meta.page} of {meta.totalPages} ({formatNumber(meta.totalRows)} rows)
          </span>
          <button onClick={() => handlePageChange(meta.page + 1)} disabled={!canGoNext || loading}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteStats;
